import React, { SyntheticEvent } from 'react'
import { isEmpty } from 'lodash'

import { BulletLabel, Skeleton } from 'components'
import { TBody, Row, ActionEventTable } from 'components/Table'

import type { IColumn, CompanyForList } from 'types'
import { getPhoneFormatter } from 'helpers/common'
import { getFullAddress } from 'helpers/address'

interface Props {
  columns: IColumn[]
  companies: CompanyForList[]
  loading: boolean
  isError: boolean
  onClickRow: (id: string) => (e: SyntheticEvent) => void
}

const SkeletonListOdd: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton width='111px' />,
  name: <Skeleton.whiteSkeleton width='154px' />,
  phone: <Skeleton.whiteSkeleton />,
  address: <Skeleton.whiteSkeleton width='291px' />,
}

const SkeletonListEven: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton />,
  name: <Skeleton.whiteSkeleton width='246px' />,
  phone: <Skeleton.whiteSkeleton />,
  address: <Skeleton.whiteSkeleton width='422px' />,
}

const CompanyList: React.FC<Props> = (props: Props) => {
  const { columns, companies, loading, isError, onClickRow } = props

  const list: Record<string, React.ReactNode>[] = companies.map((item) => {
    let address = ''

    if (item.addresses[0]) {
      address = getFullAddress('th', item.addresses[0], {
        hasCountry: false,
        separator: ', ',
      })
    }

    return {
      no: item.no,
      code: item.code,
      name: item.name,
      phones: getPhoneFormatter(item.phone) || '-',
      address,
      isActive: <BulletLabel isActive={Boolean(item.isActive)} />,
    }
  })

  if (loading) {
    return (
      <Skeleton.SkeletonList
        columns={columns}
        rowOdd={SkeletonListOdd}
        rowEven={SkeletonListEven}
      />
    )
  }

  if (isEmpty(companies) || isError) {
    return <ActionEventTable colSpan={columns.length} isNotFound />
  }

  return (
    <TBody>
      {list.map((data, index) => (
        <Row
          data-testid={`company-row-${companies[index].id}`}
          key={index}
          columns={columns}
          data={data}
          onClick={onClickRow(companies[index].id)}
        />
      ))}
    </TBody>
  )
}

export default CompanyList
